/**
 * Billing Context - Provides plan and usage data throughout the app
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { billingService } from '../services/billingService';

// Feature-gating master switch. Set to `false` while Sparvi runs on a
// single-price plan ($999/mo, May 2026+) — every paying customer sees
// every feature regardless of which tier the backend's billing model
// reports them as being on. Flip to `true` if/when tier-based pricing
// is reintroduced and the per-tier feature matrix in plans.py becomes
// authoritative again.
//
// Note this does NOT short-circuit USAGE LIMITS (max_connections,
// max_users, etc.) — those are quota-based and remain enforced by
// getLimitStatus / canPerformAction below. Only feature toggles
// (isFeatureEnabled) and tier-availability checks bypass.
//
// The full gating infrastructure (FeatureGate, UpgradePrompt,
// LOCKED-state JSX in tier-config UI) is intentionally left in place
// so re-enabling is a single-line change, not a rebuild.
export const BILLING_GATING_ACTIVE = false;

const BillingContext = createContext();

export const useBilling = () => {
  const context = useContext(BillingContext);
  if (!context) {
    throw new Error('useBilling must be used within a BillingProvider');
  }
  return context;
};

export const BillingProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();

  const [plan, setPlan] = useState(null);
  const [usage, setUsage] = useState(null);
  const [trialStatus, setTrialStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch plan, usage, and trial state in one pass
  const fetchBillingData = useCallback(async () => {
    if (!isAuthenticated) {
      setPlan(null);
      setUsage(null);
      setTrialStatus(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [planData, usageData, trialData] = await Promise.all([
        billingService.getCurrentPlan(),
        billingService.getUsage(),
        billingService.getTrialStatus(),
      ]);

      setPlan(planData);
      setUsage(usageData);
      setTrialStatus(trialData);
    } catch (err) {
      console.error('Failed to fetch billing data:', err);
      setError(err.message || 'Failed to load billing information');

      // Do NOT silently downgrade to free here. Falling back to a fake "free"
      // plan caused paying customers to be feature-gated for an entire session
      // whenever the cold-start /api/billing/plan request timed out. Leave
      // plan=null so consumers (FeatureGate / TierGate / LimitGate) can detect
      // the unknown state and let the backend remain the source of truth.
      setPlan(null);
      setUsage(null);
      setTrialStatus(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Fetch billing data on mount and when auth changes
  useEffect(() => {
    fetchBillingData();
  }, [fetchBillingData]);

  /**
   * Check if a feature is enabled for the current plan
   * @param {string} featureName - Feature to check (e.g., 'dbt_enabled')
   * @returns {boolean} Whether the feature is enabled
   */
  const isFeatureEnabled = useCallback((featureName) => {
    // Master gating switch off → every feature is on for every user.
    // See BILLING_GATING_ACTIVE comment at module top.
    if (!BILLING_GATING_ACTIVE) return true;
    // Plan unknown (load failed): assume access — backend gates real mutations.
    // Better UX than locking out a paying customer when /billing/plan times out.
    if (!plan) return true;
    return Boolean(plan?.features?.[featureName]);
  }, [plan]);

  /**
   * Check if a limit has been reached
   * @param {string} limitName - Limit to check (e.g., 'connections')
   * @returns {Object} Limit status { isAtLimit, current, max, percentage }
   */
  const getLimitStatus = useCallback((limitName) => {
    // Plan unknown (load failed): treat as unlimited so UI doesn't block actions.
    if (!plan) {
      return { is_at_limit: false, current: 0, max: -1, percentage: 0, isUnlimited: true };
    }
    if (!usage?.limit_checks?.[limitName]) {
      // Default to restricted if we don't have limit info yet
      return { is_at_limit: true, current: 0, max: 0, percentage: 0, isUnlimited: false };
    }
    return usage.limit_checks[limitName];
  }, [plan, usage]);

  /**
   * Check if user can perform an action (within limit)
   * @param {string} limitName - Limit to check
   * @returns {boolean} Whether the action is allowed
   */
  const canPerformAction = useCallback((limitName) => {
    // Plan unknown (load failed): allow — backend gates real mutations.
    if (!plan) return true;

    const status = getLimitStatus(limitName);

    // If max is 0, the feature is not available at all for this plan
    if (status.max === 0) {
      return false;
    }

    // Unlimited (-1) or not at limit
    return status.isUnlimited || status.max === -1 || !status.is_at_limit;
  }, [plan, getLimitStatus]);

  /**
   * Get the current plan tier
   * @returns {string} Plan tier name
   */
  const getPlanTier = useCallback(() => {
    return plan?.plan_tier || 'free';
  }, [plan]);

  /**
   * Get the next upgrade tier
   * @returns {string|null} Next tier or null if at highest
   */
  const getNextTier = useCallback(() => {
    return plan?.next_tier || null;
  }, [plan]);

  /**
   * Check if upgrade is available
   * @returns {boolean} Whether upgrade is available
   */
  const canUpgrade = useCallback(() => {
    return plan?.upgrade_available || false;
  }, [plan]);

  /**
   * Get upgrade URL for a feature or limit
   * @param {string} tier - Target tier (optional, defaults to next tier)
   * @returns {string} Upgrade URL
   */
  const getUpgradeUrl = useCallback((tier = null) => {
    const targetTier = tier || plan?.next_tier || 'starter';
    return `/settings/billing?upgrade=${targetTier}`;
  }, [plan]);

  /**
   * Refresh billing data
   */
  const refresh = useCallback(() => {
    return fetchBillingData();
  }, [fetchBillingData]);

  const value = {
    // Data
    plan,
    usage,
    trialStatus,
    loading,
    error,

    // Helper functions
    isFeatureEnabled,
    getLimitStatus,
    canPerformAction,
    getPlanTier,
    getNextTier,
    canUpgrade,
    getUpgradeUrl,
    refresh,

    // Convenience accessors
    planTier: plan?.plan_tier || 'free',
    planDisplayName: plan?.display_name || 'Free',
    isFoundingMember: plan?.is_founding_member || false,

    // Trial accessors (sourced from /api/billing/trial-status)
    isTrialActive: Boolean(trialStatus?.is_trial),
    trialDaysRemaining: trialStatus?.days_remaining ?? null,
    trialExpiresAt: trialStatus?.expires_at ?? null,
    wasInTrial: Boolean(trialStatus?.was_in_trial),
    trialConvertedAt: trialStatus?.converted_at ?? null,
    // True when the org was in a trial and is no longer in one and never converted.
    // Useful for the trial-expired UX in feature gates and the in-app banner.
    isTrialExpired: Boolean(
      trialStatus && trialStatus.was_in_trial && !trialStatus.is_trial && !trialStatus.converted_at
    ),
  };

  return (
    <BillingContext.Provider value={value}>
      {children}
    </BillingContext.Provider>
  );
};

export default BillingContext;
